/**
 * PostHog wiring + assistant-specific captures.
 *
 * Loaded once on app boot. Initializes posthog-js with session replay on,
 * identifies the customer if userData is in localStorage, and exposes a
 * small set of capture helpers used by useAssistantSession + GovavaAIGift.
 *
 * Convention: every assistant event includes `assistant_session_id` so all
 * events for one Talk-to-Ava session land in the same PostHog timeline,
 * tying the replay video, LLM traces, and our custom events together.
 */

import posthog from 'posthog-js';

const POSTHOG_KEY  = process.env.VUE_APP_POSTHOG_KEY  || 'phc_xECb8Qz6jwWbKTqTqsiaamf376bv2hdCDe8Sa6NxS8oz';
const POSTHOG_HOST = process.env.VUE_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

export function initPostHog() {
  if (initialized || typeof window === 'undefined') return;
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      // Session replay on. Default privacy: mask all input fields by default
      // so passwords/payment never leak. We can opt-in specific fields later.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '.ph-mask',
      },
      // Capture console.log/warn/error inside session replay. The
      // project-level capture_console_log_opt_in is already on, but
      // posthog-js still needs this explicit flag to actually emit.
      enable_recording_console_log: true,
      // Capture network/performance entries inside session replay so we
      // can see XHR/fetch waterfalls alongside the screen recording.
      capture_performance: true,
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      // Sentry-equivalent: globally capture uncaught errors + unhandled
      // promise rejections. They land in PostHog Error Tracking with
      // stack traces, tied to the same distinct_id and session replay.
      capture_exceptions: true,
      // Send beacon-style on tab close so we don't lose the last events.
      // (PostHog's default already does this; explicit for clarity.)
      request_batching: true,
    });
    initialized = true;
    // Make posthog accessible to non-Vue modules (e.g. axios interceptor in
    // utils/api.js) so they can forward $session_id / distinct_id to Laravel.
    if (typeof window !== 'undefined') {
      window.posthog = posthog;
    }
    identifyFromLocalStorage();
  } catch (e) {
    console.warn('PostHog init failed:', e);
  }
}

function identifyFromLocalStorage() {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    // LoginView stores: { user: data.customer, status: true }. Real id lives at user.id.
    // Keep fallbacks for older shapes.
    const u = (parsed && (parsed.user || parsed.customer)) || parsed;
    const id = u && (u.id || u.customer_id);
    const email = u && (u.email || u.username);
    const name = u && (u.name || u.first_name);
    if (id) {
      const props = {};
      if (email) props.email = email;
      if (name) props.name = name;
      posthog.identify(String(id), props);
    }
  } catch (e) { /* ignore */ }
}

export function ph(event, properties) {
  if (!initialized) return;
  try { posthog.capture(event, properties || {}); } catch (e) { /* ignore */ }
}

export function phSetSession(sessionId) {
  if (!initialized) return;
  try {
    posthog.register({ assistant_session_id: sessionId });
  } catch (e) { /* ignore */ }
}

export function phClearSession() {
  if (!initialized) return;
  try {
    posthog.unregister('assistant_session_id');
  } catch (e) { /* ignore */ }
}

export { posthog };
